import ReactECharts from 'echarts-for-react';
import type { StyleConfig } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';

interface GaugeChartProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  measures: { alias: string }[];
  style?: StyleConfig;
  min?: number;
  max?: number;
}

export default function GaugeChart({ data, measures, style, min = 0, max = 100 }: GaugeChartProps) {
  const theme = useEChartsTheme();

  const measureKey = measures[0]?.alias || data.columns[0];
  const value = Number(data.rows[0]?.[measureKey]) || 0;

  // Colour zones: red < 40%, amber 40-70%, green > 70%
  const redEnd = (style as Record<string, unknown>)?.red_threshold as number | undefined;
  const greenStart = (style as Record<string, unknown>)?.green_threshold as number | undefined;
  const zones = [
    { min: 0, max: redEnd ?? 0.4, color: '#e74c3c' },
    { min: redEnd ?? 0.4, max: greenStart ?? 0.7, color: '#f39c12' },
    { min: greenStart ?? 0.7, max: 1, color: '#27ae60' },
  ];

  const option = {
    tooltip: { formatter: `{b}: {c}` },
    series: [
      {
        type: 'gauge' as const,
        min,
        max,
        progress: { show: true, width: 14 },
        axisLine: {
          lineStyle: {
            width: 14,
            color: zones.map((z) => [z.max, z.color] as [number, string]),
          },
        },
        axisTick: { show: false },
        splitLine: { length: 10, lineStyle: { width: 2 } },
        pointer: { itemStyle: { color: 'auto' } },
        detail: {
          valueAnimation: true,
          fontSize: 24,
          offsetCenter: [0, '60%'],
          formatter: '{value}',
        },
        data: [{ value, name: measures[0]?.alias || '' }],
      },
    ],
  };

  return <ReactECharts option={option} theme={theme} style={{ height: '100%', width: '100%' }} />;
}
